import React, { useState, useEffect } from 'react';
import { Plus, Users, Mail, UserCheck, AlertCircle, CheckCircle, Loader2, Search, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../components/ui/Button';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import UserManagementList from '../components/UserManagementList';
import AssociateClientModal from '../components/AssociateClientModal';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  client_name: string | null;
  created_at: string;
}

interface Client {
  id: string;
  name: string;
}

interface UserWithClient {
  user_id: string;
  role: string;
  email: string | null;
  sales_name: string | null;
  client_id: string | null;
  clients: {
    id: string;
    name: string;
    company_name: string | null;
  } | null;
}

const UsersPage: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<'admin' | 'client'>('client');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  
  // Data state
  const [clients, setClients] = useState<Client[]>([]);
  
  // UI state
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // User-Client association state
  const [selectedUser, setSelectedUser] = useState<UserWithClient | null>(null);
  const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Redirect if not super admin
  useEffect(() => {
    if (profile && profile.role !== 'super_admin') {
      navigate('/');
    }
  }, [profile, navigate]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Fetch clients for the dropdown
        const { data: clientData } = await supabase
          .from('clients')
          .select('id, name')
          .order('name');

        if (clientData) {
          setClients(clientData);
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (profile?.role === 'super_admin') {
      fetchData();
    }
  }, [profile]);

  // Fonction pour valider l'email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsInviting(true);

    try {
      // 1. Récupérer les valeurs du formulaire
      const formEmail = email.trim();
      const formRole = selectedRole;
      const formClientId = selectedRole === 'client' ? selectedClientId : null;

      console.log("🔍 Valeurs du formulaire :", {
        email: formEmail,
        role: formRole,
        selectedClientId,
        formClientId,
        isClient: selectedRole === 'client'
      });

      // 2. Vérifications de validation
      if (!formEmail) {
        throw new Error('L\'email est requis');
      }

      if (!isValidEmail(formEmail)) {
        throw new Error('Veuillez entrer une adresse email valide');
      }

      if (!formRole) {
        throw new Error('Veuillez sélectionner un rôle');
      }

      if (formRole === 'client' && !formClientId) {
        throw new Error('Veuillez sélectionner un client pour le rôle client');
      }

      // 3. Récupérer le token JWT de la session en cours
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Session non valide. Veuillez vous reconnecter.');
      }

      const accessToken = session.access_token;

      // 4. Appeler la Supabase Edge Function
      const payload = {
        email: formEmail,
        role: formRole,
        client_id: formClientId
      };
      console.log("📤 Payload envoyé à l'Edge Function :", payload);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log("📥 Réponse de l'Edge Function :", { status: response.status, result });

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'invitation');
      }

      // 5. Afficher une notification de succès
      setSuccess(`Invitation envoyée avec succès à ${formEmail}`);
      
      // 8. Remettre à zéro les champs du formulaire
      setEmail('');
      setSelectedRole('client');
      setSelectedClientId('');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error inviting user:', err);
      setError(err.message || 'Erreur lors de l\'invitation');
    } finally {
      // 7. Réactiver le bouton après le chargement
      setIsInviting(false);
    }
  };

  const handleAssignClient = (user: UserWithClient) => {
    setSelectedUser(user);
    setIsAssociateModalOpen(true);
  };

  const handleAssociationSuccess = () => {
    setRefreshTrigger(prev => prev + 1);
    setSuccess('Association mise à jour avec succès');
    setTimeout(() => setSuccess(null), 3000);
  };

  if (profile?.role !== 'super_admin') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Chargement..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-purple-600 bg-clip-text text-transparent">
              Gestion des utilisateurs
            </h1>
            <p className="text-gray-600 mt-2 text-lg">
              Invitez de nouveaux utilisateurs sur la plateforme
            </p>
          </div>
        </motion.div>

        {/* Invitation Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Inviter un nouvel utilisateur</h2>
          
          <form onSubmit={handleInviteUser} className="space-y-6">
            {error && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center"
              >
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center"
              >
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                {success}
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isInviting}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200"
                  placeholder="utilisateur@exemple.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserCheck className="h-4 w-4 inline mr-1" />
                  Rôle
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value as 'admin' | 'client');
                    setSelectedClientId('');
                  }}
                  disabled={isInviting}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <option value="admin">Administrateur</option>
                  <option value="client">Client</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4 inline mr-1" />
                  Client
                </label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={selectedRole === 'admin' || isInviting}
                  required={selectedRole === 'client'}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <option value="">
                    {selectedRole === 'admin' ? 'Non applicable' : 'Sélectionner un client'}
                  </option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isInviting}
                icon={isInviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
              >
                {isInviting ? 'Invitation en cours...' : 'Inviter l\'utilisateur'}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* User-Client Association Management Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Gérer les associations utilisateur-client</h2>
            <p className="text-gray-600 text-sm">
              Assignez ou modifiez les clients associés aux utilisateurs existants
            </p>
          </div>

          <UserManagementList
            onAssignClient={handleAssignClient}
            refreshTrigger={refreshTrigger}
          />
        </motion.div>

        {/* Associate Client Modal */}
        <AssociateClientModal
          isOpen={isAssociateModalOpen}
          onClose={() => {
            setIsAssociateModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSuccess={handleAssociationSuccess}
        />
      </div>
    </div>
  );
};

export default UsersPage;